import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PlusCircle, Search, User, Sun, Moon, Home, LayoutDashboard } from 'lucide-react';
import { RolePickerModal } from './RolePickerModal';
import { useDemands } from '../hooks/useDemands';
// Lazy: modal-only component whose static import pulled the Google Maps
// vendor chunk into the entry bundle (audit: unused JS).
const DemandForm = React.lazy(() =>
  import('./demands/DemandForm').then(m => ({ default: m.DemandForm }))
);
import { UserButton } from '@clerk/clerk-react';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { CLS_CONTAINER, CITY_HUB_LINKS, INTENT_HUB_LINKS } from '../constants';
import { useHashRouter } from '../hooks/useHashRouter';
import { Logo } from './Logo';
import { ContactModal } from './ContactModal';
import { api } from '../services/d1';
import { useTheme } from '../context/ThemeContext';
import { ContactModalProvider } from '../context/ContactModalContext';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { navigate, route } = useHashRouter();
  const { user, isSignedIn, isLoaded } = useAuth();
  const { myQuery } = useDemands();
  const myOpenDemands = myQuery.data?.filter(d => d.status === 'open').length ?? 0;
  const [showRolePicker, setShowRolePicker] = useState(false);
  const [showDemandForm, setShowDemandForm] = useState(false);
  const [demandFormInitialValues, setDemandFormInitialValues] = useState<any>(undefined);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const openContactModal = useCallback(() => setIsContactModalOpen(true), []);
  const { isDark, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleOpenFormEvent = (e: any) => {
      if (window.location.pathname.startsWith('/market')) return; // handled locally by Marketplace
      if (e?.detail) {
        setDemandFormInitialValues(e.detail);
      }
      setShowDemandForm(true);
    };
    window.addEventListener('espacero_open_demand_form', handleOpenFormEvent);
    return () => window.removeEventListener('espacero_open_demand_form', handleOpenFormEvent);
  }, []);

  const { t } = useTranslation(["common", "home", "search", "listing", "profile", "add_listing", "admin"]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-sync Clerk user to D1 on sign-in (once per session per user)
  useEffect(() => {
    if (user?.id && !sessionStorage.getItem(`user_synced_v3_${user.id}`)) {
      const email = user.email || '';
      const name = user.name || '';
      // Read role chosen during signup (set by signup flow before Clerk redirect)
      const signupRole = sessionStorage.getItem('signup_role') as 'owner' | 'seeker' | 'vendor' | null;
      api.auth.sync({ email, name, role: signupRole }).then((res) => {
        sessionStorage.setItem(`user_synced_v3_${user.id}`, '1');
        // Store role in sessionStorage so useAuth can read it without extra API call, and dispatch reactive event (Finding P2)
        if (res?.role) {
          sessionStorage.setItem(`espacero_user_role_${user.id}`, res.role);
          window.dispatchEvent(new CustomEvent('espacero_role_changed', { detail: { userId: user.id, role: res.role } }));
        }
        // Cleanup one-time signup role
        if (signupRole) {
          sessionStorage.removeItem('signup_role');
        }
      }).catch(e => console.error('User sync failed:', e));
    }
  }, [user?.id, user?.email]);

  const isActive = (path: string) => {
    if (path.includes('demands=1')) {
      return route.startsWith('/market') && (window.location.search.includes('demands=1') || window.location.search.includes('tab=demands'));
    }
    const cleanPath = path.replace(/\/$/, '') || '/';
    const cleanRoute = route.replace(/\/$/, '') || '/';
    if (cleanPath === '/market' && cleanRoute === '/market') {
      if (window.location.search.includes('demands=1') || window.location.search.includes('tab=demands')) return false;
      return true;
    }
    if (cleanPath === '/' && cleanRoute === '/') return true;
    return cleanPath !== '/' && cleanRoute.startsWith(cleanPath);
  };

  const isAdminRoute = route.startsWith('/admin');

  const navItems = [
    { label: t('nav.market'),        path: '/market/' },
    { label: 'Nástenka dopytov',     path: '/market?demands=1' },
    { label: t('nav.vendors'),       path: '/sluzby/' },
    { label: t('nav.how_it_works'),  path: '/how-it-works/' },
  ];

  const BottomNavItem = ({ icon: Icon, label, path, active, onClick }: any) => (
    <a
      href={path}
      onClick={(e) => {
        if (onClick) { e.preventDefault(); onClick(); }
        else         { e.preventDefault(); navigate(path); }
      }}
      className={`relative flex flex-col items-center justify-center w-full py-2 transition-all ${
        active
          ? 'text-accent-terra'
          : 'text-stone-500 dark:text-stone-400 hover:text-stone-600 dark:hover:text-stone-300'
      }`}
    >
      <div className={`p-1.5 rounded-xl transition-all ${active ? 'bg-orange-50 dark:bg-stone-800' : ''}`}>
        <Icon size={20} strokeWidth={active ? 2.5 : 2} />
      </div>
      <span className={`text-[10px] font-semibold mt-0.5 ${active ? 'opacity-100' : 'opacity-60'}`}>
        {label}
      </span>
      {active && (
        <span className="absolute top-0 w-8 h-0.5 bg-accent-terra rounded-b-full" />
      )}
    </a>
  );

  return (
    <div className="min-h-screen flex flex-col font-sans text-primary dark:text-stone-200 pb-20 md:pb-0 transition-colors duration-300">

      {/* ── Desktop Header ── */}
      <header
        className={`hidden md:block sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/90 dark:bg-stone-950/90 backdrop-blur-xl border-b border-stone-200/70 dark:border-stone-800/60 shadow-nav'
            : 'bg-white/80 dark:bg-stone-950/80 backdrop-blur-md border-b border-stone-100/80 dark:border-stone-900/80'
        }`}
      >
        <div className={CLS_CONTAINER}>
          <div className="flex justify-between items-center h-[68px]">

            {/* Logo */}
            <div className="flex items-center cursor-pointer group flex-shrink-0" onClick={() => navigate('/')}>
              <div className="group-hover:scale-105 transition-transform duration-300">
                <Logo size="lg" />
              </div>
            </div>

            {/* Nav pills */}
            <nav className="flex items-center gap-0.5 lg:gap-1">
              {navItems.map(item => (
                <a
                  key={item.path}
                  href={item.path}
                  onClick={(e) => { e.preventDefault(); navigate(item.path); }}
                  className={`relative px-2.5 lg:px-4 py-2 rounded-xl text-xs lg:text-sm font-semibold transition-all duration-200 whitespace-nowrap ${
                    isActive(item.path)
                      ? 'text-accent-terra'
                      : 'text-stone-500 dark:text-stone-400 hover:text-primary dark:hover:text-stone-100 hover:bg-stone-100/60 dark:hover:bg-stone-800/50'
                  }`}
                >
                  {item.label}
                  {isActive(item.path) && (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-accent-terra" />
                  )}
                </a>
              ))}
              <button
                onClick={() => setShowDemandForm(true)}
                className="px-2.5 lg:px-4 py-2 rounded-xl text-xs lg:text-sm font-bold text-accent-terra hover:bg-orange-50/50 dark:hover:bg-stone-800/50 transition-all duration-200 whitespace-nowrap"
              >
                Zadať dopyt
              </button>
            </nav>

            {/* Desktop Actions */}
            <div className="flex items-center gap-1.5 lg:gap-2 flex-shrink-0">
              {/* Theme Toggle */}
              <button
                type="button"
                onClick={toggleTheme}
                className="flex-shrink-0 p-2.5 rounded-xl text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 hover:text-stone-700 dark:hover:text-stone-200 cursor-pointer touch-manipulation active:scale-95 transition-all duration-200 select-none"
                title={isDark ? 'Prepnúť na svetlý režim' : 'Prepnúť na tmavý režim'}
                aria-label={isDark ? 'Svetlý režim' : 'Tmavý režim'}
              >
                {isDark ? <Sun size={18} /> : <Moon size={18} />}
              </button>

              {isSignedIn && !isAdminRoute && user?.role !== 'vendor' && (
                <button
                  onClick={() => navigate('/dashboard')}
                  className={`relative flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all border border-stone-300 dark:border-stone-700 ${
                    route.startsWith('/dashboard')
                      ? 'text-primary dark:text-amber-400 bg-stone-100 dark:bg-stone-800'
                      : 'text-stone-500 dark:text-stone-400 hover:text-primary dark:hover:text-stone-100 hover:bg-stone-100/60 dark:hover:bg-stone-800/50'
                  }`}
                  title="Dashboard"
                >
                  <LayoutDashboard size={15} />
                  <span className="hidden xl:inline">Dashboard</span>
                  {myOpenDemands > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary text-white text-[9px] font-black flex items-center justify-center">
                      {myOpenDemands}
                    </span>
                  )}
                </button>
              )}

              {isLoaded && !isSignedIn && !isAdminRoute && (
                <>
                  <button
                    onClick={() => navigate('/login')}
                    className="px-4 py-2 rounded-xl text-sm font-semibold text-stone-500 dark:text-stone-400 hover:text-primary dark:hover:text-stone-100 hover:bg-stone-100/60 dark:hover:bg-stone-800/50 transition-all border border-stone-300 dark:border-stone-700"
                  >
                    Prihlásiť sa
                  </button>
                  <button
                    onClick={() => setShowRolePicker(true)}
                    className="flex items-center gap-2 btn-primary-gradient px-5 py-2.5 rounded-full font-bold text-sm group shadow-sm hover:shadow-glow-terra transition-all"
                  >
                    Začnite tu
                  </button>
                </>
              )}

              {isLoaded && isSignedIn && !isAdminRoute && (
                <button
                  onClick={() => {
                    if (user?.role === 'seeker') navigate('/dashboard');
                    else if (user?.role === 'vendor') navigate('/sluzby/pridat');
                    else navigate('/add');
                  }}
                  className="flex items-center gap-2 btn-primary-gradient px-5 py-2.5 rounded-full font-bold text-sm group shadow-sm hover:shadow-glow-terra transition-all"
                >
                  <PlusCircle size={17} className="group-hover:rotate-90 transition-transform duration-300" />
                  <span>
                    {user?.role === 'seeker' ? 'Pridať dopyt' : user?.role === 'vendor' ? 'Pridať službu' : t('nav.add_listing')}
                  </span>
                </button>
              )}

              {isLoaded && isSignedIn && (
                <div className="flex items-center gap-2 pl-3 border-l border-stone-200/70 dark:border-stone-700/50 ml-1">
                  <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/profile')} className="hover:text-accent-terra transition-colors">
                      <span className="hidden lg:block text-xs font-bold text-stone-600 dark:text-stone-300">{user?.name}</span>
                    </button>
                    <UserButton afterSignOutUrl="/" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── Mobile Header ── */}
      <header className="md:hidden bg-white/90 dark:bg-stone-900/90 backdrop-blur-xl sticky top-0 z-40 border-b border-stone-100 dark:border-stone-800">
        <div className="px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <Logo size="md" />
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">

            {isLoaded && !isSignedIn && !isAdminRoute && (
              <button
                onClick={() => navigate('/login')}
                className="px-3 py-1.5 rounded-xl text-xs font-semibold text-stone-600 dark:text-stone-300 hover:text-primary dark:hover:text-stone-100 bg-stone-100/80 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 transition-all"
              >
                Prihlásiť sa
              </button>
            )}

            <button
              type="button"
              onClick={toggleTheme}
              className="p-2 rounded-xl text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 cursor-pointer touch-manipulation active:scale-95 transition-all duration-200 select-none"
              title={isDark ? 'Svetlý režim' : 'Tmavý režim'}
              aria-label={isDark ? 'Svetlý režim' : 'Tmavý režim'}
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {user && (
              <div className="flex items-center cursor-pointer" onClick={() => navigate('/profile')}>
                <UserButton afterSignOutUrl="/" />
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Main Content ── */}
      <main className="flex-grow w-full">
        {useMemo(() => (
          <ContactModalProvider value={openContactModal}>
            {children}
          </ContactModalProvider>
        ), [openContactModal, children])}
      </main>

      {/* ── Mobile Bottom Nav ── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/92 dark:bg-stone-900/92 backdrop-blur-2xl border-t border-stone-200 dark:border-stone-800 pb-safe z-50 shadow-[0_-4px_20px_-8px_rgba(28,25,23,0.1)]">
        <div className={`grid ${isAdminRoute ? 'grid-cols-3' : (isSignedIn && user?.role !== 'vendor') ? 'grid-cols-5' : 'grid-cols-4'} items-end h-[64px] pb-2 px-1`}>

          <BottomNavItem icon={Home} label={t('nav.home')} path="/" active={route === '/'} />

          <BottomNavItem icon={Search} label={t('nav.market')} path="/market" active={route.startsWith('/market')} />

          {isSignedIn && !isAdminRoute && user?.role !== 'vendor' && (
            <div className="relative flex flex-col items-center justify-center w-full py-2">
              <BottomNavItem icon={LayoutDashboard} label="Dashboard" path="/dashboard" active={route.startsWith('/dashboard')} />
              {myOpenDemands > 0 && (
                <span className="absolute top-1 left-1/2 ml-2 w-3.5 h-3.5 rounded-full bg-primary text-white text-[8px] font-black flex items-center justify-center">
                  {myOpenDemands}
                </span>
              )}
            </div>
          )}

          {/* Center: Add button or Zadať dopyt */}
          {!isAdminRoute && (
            <div className="flex flex-col items-center justify-center relative -top-3">
              <button
                onClick={() => {
                  if (!isSignedIn || user?.role === 'seeker') {
                    setShowDemandForm(true);
                  } else if (user?.role === 'vendor') {
                    navigate('/sluzby/pridat');
                  } else {
                    navigate('/add');
                  }
                }}
                className="btn-primary-gradient p-3 rounded-2xl shadow-xl active:scale-95 transition-all duration-200"
                title={isSignedIn && user?.role === 'vendor' ? 'Pridať službu' : isSignedIn && user?.role === 'owner' ? t('nav.add_listing') : 'Zadať dopyt'}
              >
                <PlusCircle size={22} strokeWidth={2.5} />
              </button>
              <span className="text-[10px] font-semibold mt-1 text-stone-500 dark:text-stone-400">
                {isSignedIn && user?.role === 'vendor' ? 'Služba' : isSignedIn && user?.role === 'owner' ? t('nav.add_listing') : 'Zadať dopyt'}
              </span>
            </div>
          )}

          <button
            onClick={() => isSignedIn ? navigate('/profile') : navigate('/login')}
            className={`relative flex flex-col items-center justify-center w-full py-2 transition-all ${
              route.startsWith('/profile') ? 'text-accent-terra' : 'text-stone-500 dark:text-stone-400'
            }`}
          >
            {route.startsWith('/profile') && <span className="absolute top-0 w-8 h-0.5 bg-accent-terra rounded-b-full" />}
            <div className={`p-1.5 rounded-xl relative transition-all ${route.startsWith('/profile') ? 'bg-orange-50 dark:bg-stone-800' : ''}`}>
              <User size={20} strokeWidth={route.startsWith('/profile') ? 2.5 : 2} />
            </div>
            <span className="text-[10px] font-semibold mt-0.5 opacity-60">{t('nav.profile')}</span>
          </button>

        </div>
      </nav>

      {/* ── Footer ── */}
      <footer className="bg-white dark:bg-stone-900 border-t border-stone-100 dark:border-stone-800 pt-14 pb-32 md:pb-14 mt-auto">
        <div className={CLS_CONTAINER}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">

            {/* Left Column */}
            <div className="pr-6">
              <div className="flex items-center gap-3 mb-6 cursor-pointer" onClick={() => navigate('/')}>
                <Logo size="lg" />
              </div>
              <p className="text-sm text-stone-500 dark:text-stone-400 leading-relaxed font-medium mb-6">
                {t('footer.tagline')}
              </p>
              <div className="text-[11px] text-stone-500 dark:text-stone-400 leading-relaxed space-y-0.5 mb-6 border-l-2 border-stone-200 dark:border-stone-800 pl-3">
                <p className="font-bold text-stone-500 dark:text-stone-400">Prevádzkovateľ portálu:</p>
                <p>MedCode s. r. o., Suché mýto 515/6, 811 03 Bratislava</p>
                <p>IČO: 57 257 400 | DIČ: 1077648132</p>
                <p>Zapísaná v OR Mestského súdu Bratislava III, Odd. Sro, Vl. č. 192497/B</p>
              </div>
              <div className="flex items-center gap-4">
                <a
                  href="https://www.facebook.com/profile.php?id=61582798618265"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-stone-500 hover:text-accent-terra transition-colors"
                  aria-label="Facebook"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-bold text-accent-terra mb-6 text-sm uppercase tracking-wider">{t('footer.support')}</h4>
              <ul className="space-y-3 text-sm text-stone-500 dark:text-stone-400 font-medium">
                <li><a href="/blog/" onClick={(e) => { e.preventDefault(); navigate('/blog/'); }} className="hover:text-accent-terra transition-colors">{t('footer.blog')}</a></li>
                <li><a href="/sluzby/" onClick={(e) => { e.preventDefault(); navigate('/sluzby/'); }} className="hover:text-accent-terra transition-colors">{t('nav.vendors')}</a></li>
                <li><a href="/how-it-works/" onClick={(e) => { e.preventDefault(); navigate('/how-it-works/'); }} className="hover:text-accent-terra transition-colors">{t('nav.how_it_works')}</a></li>
                <li><a href="/overenie/" onClick={(e) => { e.preventDefault(); navigate('/overenie/'); }} className="hover:text-accent-terra transition-colors">Ako overujeme priestory</a></li>
                <li><a href="/report/eventove-priestory-2026/" onClick={(e) => { e.preventDefault(); navigate('/report/eventove-priestory-2026/'); }} className="hover:text-accent-terra transition-colors">Dátový report 2026</a></li>
                <li><a href="/pricing/" onClick={(e) => { e.preventDefault(); navigate('/pricing/'); }} className="hover:text-accent-terra transition-colors">{t('nav.pricing')}</a></li>
                <li><a href="/terms/" onClick={(e) => { e.preventDefault(); navigate('/terms/'); }} className="hover:text-accent-terra transition-colors">{t('footer.terms')}</a></li>
                <li><a href="/privacy/" onClick={(e) => { e.preventDefault(); navigate('/privacy/'); }} className="hover:text-accent-terra transition-colors">{t('footer.privacy')}</a></li>
                <li><a href="/cookies/" onClick={(e) => { e.preventDefault(); navigate('/cookies/'); }} className="hover:text-accent-terra transition-colors">{t('footer.cookies')}</a></li>
                <li><a href="/sitemap-html/" onClick={(e) => { e.preventDefault(); navigate('/sitemap-html/'); }} className="hover:text-accent-terra transition-colors">Mapa priestorov</a></li>
                <li>
                  <button onClick={() => setIsContactModalOpen(true)} className="hover:text-accent-terra transition-colors text-left">
                    {t('footer.contact')}
                  </button>
                </li>
              </ul>
            </div>

            {/* Priestory podľa mesta — interné odkazy na mestské hub stránky */}
            <div>
              <h4 className="font-bold text-accent-terra mb-6 text-sm uppercase tracking-wider">{t('footer.by_city')}</h4>
              <ul className="space-y-3 text-sm text-stone-500 dark:text-stone-400 font-medium">
                {CITY_HUB_LINKS.map(link => (
                  <li key={link.to}>
                    <a href={link.to} onClick={(e) => { e.preventDefault(); navigate(link.to); }} className="hover:text-accent-terra transition-colors">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Obľúbené výbery — interné odkazy na intent landing pages */}
            <div>
              <h4 className="font-bold text-accent-terra mb-6 text-sm uppercase tracking-wider">{t('footer.popular_picks')}</h4>
              <ul className="space-y-3 text-sm text-stone-500 dark:text-stone-400 font-medium">
                {INTENT_HUB_LINKS.map(link => (
                  <li key={link.to}>
                    <a href={link.to} onClick={(e) => { e.preventDefault(); navigate(link.to); }} className="hover:text-accent-terra transition-colors">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </footer>

      <ContactModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
      {showRolePicker && <RolePickerModal onClose={() => setShowRolePicker(false)} />}
      {showDemandForm && (
        <React.Suspense
          fallback={
            <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-fadeIn">
              <div className="bg-white dark:bg-stone-900 rounded-2xl p-5 shadow-2xl flex items-center gap-3 border border-stone-200 dark:border-stone-800">
                <div className="w-5 h-5 border-2 border-accent-terra border-t-transparent rounded-full animate-spin" />
                <span className="text-xs sm:text-sm font-bold text-stone-700 dark:text-stone-200">Načítavam formulár...</span>
              </div>
            </div>
          }
        >
          <DemandForm
            initialValues={demandFormInitialValues}
            onClose={() => {
              setShowDemandForm(false);
              setDemandFormInitialValues(undefined);
            }}
          />
        </React.Suspense>
      )}
    </div>
  );
};
